import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axios from 'axios'
import { setTokens, clearTokens, onLogout, onPasswordChangeRequired } from '@/services/api'
import { decodeJwt } from '@/lib/jwt'
import type { AuthState } from '@/types/auth'

interface AuthActions {
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshAuth: () => Promise<boolean>
  setTokensFromRefresh: (access: string, refresh: string) => void
  clearMustChangePassword: () => void
}

type AuthStore = AuthState & AuthActions

/**
 * Authentication store — manages JWT tokens, user info, and login/logout.
 *
 * - Login: POST /auth/login with OAuth2 form data, decode JWT for scopes
 * - Logout: POST /auth/logout, clear tokens, reset state
 * - Refresh: POST /auth/refresh, update tokens, return success/failure
 *
 * State is persisted to localStorage via Zustand persist middleware.
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      token: null,
      refreshToken: null,
      admin: null,
      isAuthenticated: false,
      mustChangePassword: false,

      // Actions
      login: async (email: string, password: string) => {
        // OAuth2PasswordRequestForm expects x-www-form-urlencoded
        const formData = new URLSearchParams()
        formData.append('username', email)
        formData.append('password', password)

        const { data } = await axios.post('/api/auth/login', formData, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        })

        const access = data.access_token as string
        const refresh = data.refresh_token as string
        const adminName = data.admin_name as string

        // Decode JWT to extract scopes and tenant info
        const payload = decodeJwt(access)

        // Sync tokens with the HTTP client's interceptor
        setTokens(access, refresh)

        set({
          token: access,
          refreshToken: refresh,
          isAuthenticated: true,
          mustChangePassword: data.must_change_password ?? false,
          admin: {
            sub: payload?.sub || '',
            tenant_id: payload?.tenant_id || data.tenant_id || '',
            admin_name: adminName,
            scopes: payload?.scopes || [],
            permissions: payload?.permissions || data.permissions || [],
            role: payload?.role || data.role || 'Sin rol',
          },
        })
      },

      logout: async () => {
        const token = get().token
        // Best-effort token revocation
        if (token) {
          try {
            await axios.post('/api/auth/logout', {}, {
              headers: { Authorization: `Bearer ${token}` },
            })
          } catch {
            // Ignore errors — token might already be invalid
          }
        }

        clearTokens()
        set({
          token: null,
          refreshToken: null,
          admin: null,
          isAuthenticated: false,
          mustChangePassword: false,
        })
      },

      clearMustChangePassword: () => set({ mustChangePassword: false }),

      refreshAuth: async (): Promise<boolean> => {
        const currentRefresh = get().refreshToken
        if (!currentRefresh) return false

        try {
          const { data } = await axios.post('/api/auth/refresh', null, {
            headers: { Authorization: `Bearer ${currentRefresh}` },
          })

          const access = data.access_token as string
          const refresh = data.refresh_token as string
          const payload = decodeJwt(access)

          if (!payload) return false

          setTokens(access, refresh)

          set({
            token: access,
            refreshToken: refresh,
            isAuthenticated: true,
            admin: {
              sub: payload.sub,
              tenant_id: payload.tenant_id,
              admin_name: data.admin_name || get().admin?.admin_name || '',
              scopes: payload.scopes || [],
              permissions: payload.permissions || data.permissions || get().admin?.permissions || [],
              role: payload.role || data.role || get().admin?.role || 'Sin rol',
            },
          })
          return true
        } catch {
          await get().logout()
          return false
        }
      },

      setTokensFromRefresh: (access: string, refresh: string) => {
        const payload = decodeJwt(access)
        setTokens(access, refresh)
        set({
          token: access,
          refreshToken: refresh,
          isAuthenticated: true,
          admin: payload
            ? {
                sub: payload.sub,
                tenant_id: payload.tenant_id,
                admin_name: get().admin?.admin_name || '',
                scopes: payload.scopes || [],
                permissions: payload.permissions || get().admin?.permissions || [],
                role: payload.role || get().admin?.role || 'Sin rol',
              }
            : get().admin,
        })
      },
    }),
    {
      name: 'qr-auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        admin: state.admin,
        isAuthenticated: state.isAuthenticated,
        mustChangePassword: state.mustChangePassword,
      }),
      onRehydrateStorage: () => (state) => {
        // After Zustand persist rehydrates from localStorage, sync the
        // module-level token references in api.ts so that the Axios request
        // interceptor can attach the Bearer header on the very first request.
        //
        // Without this, a page reload causes _accessToken/_refreshToken to
        // remain null (module vars reset on reload), while Zustand's store
        // correctly has the persisted values — leading to unauthenticated API
        // calls → 401 → logout → redirect to /login.
        if (state?.token && state?.refreshToken) {
          setTokens(state.token, state.refreshToken)
        }
      },
    },
  ),
)

// Register logout callback with the HTTP client
onLogout(() => {
  useAuthStore.getState().logout()
})

// Cuando el backend exige cambio de clave (403 + bandera), marcamos el estado;
// AuthLayout redirige a la pantalla de cambio obligatorio en el próximo render.
onPasswordChangeRequired(() => {
  if (!useAuthStore.getState().mustChangePassword) {
    useAuthStore.setState({ mustChangePassword: true })
  }
})
